import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useTheme } from '@/contexts/ThemeContext';
import { wikiApi, type WikiPageView, type WikiRevisionEntry } from '../../services/wiki-api';
import { handleApiError } from '../../services/api';

/**
 * Wikipedia-style revision list (D27). Changes are already live (D45):
 * Keep acknowledges (bumps the seen watermark), Revert restores the prior
 * text as a new revision. Rows expand to a GitHub-style diff (client-side,
 * D48).
 */
const HistoryView: React.FC<{ page: WikiPageView }> = ({ page }) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['wikiRevisions', page.id],
    queryFn: () => wikiApi.getRevisions(page.id),
  });

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['wikiPage', page.id] });
    queryClient.invalidateQueries({ queryKey: ['wikiRevisions', page.id] });
    queryClient.invalidateQueries({ queryKey: ['wikiTree'] });
  };

  const keep = useMutation({
    mutationFn: (version: number) => wikiApi.markSeen(page.id, version),
    onSuccess: invalidate,
    onError: (err) => toast.error(handleApiError(err)),
  });

  const revert = useMutation({
    mutationFn: (version: number) => wikiApi.revert(page.id, version),
    onSuccess: ({ version }) => {
      toast.success(`Restored as v${version}`);
      invalidate();
    },
    onError: (err) => toast.error(handleApiError(err)),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  const unseenCount = data.revisions.filter((r) => r.unseen).length;

  return (
    <div className="px-5 py-4">
      <p className="text-[11.5px] text-muted-foreground mb-2.5">
        {unseenCount > 0 && (
          <>
            <b className="text-info">
              {unseenCount} {unseenCount === 1 ? 'revision is' : 'revisions are'} new since your
              last visit
            </b>{' '}
            (blue).{' '}
          </>
        )}
        Changes are already live — Keep acknowledges, Revert restores the prior text as a new
        revision. Click any row for the diff.
      </p>

      {data.revisions.map((rev) => (
        <RevisionRow
          key={rev.id}
          page={page}
          rev={rev}
          isCurrent={rev.version === data.currentVersion}
          onKeep={() => keep.mutate(rev.version)}
          onRevert={(toVersion) => revert.mutate(toVersion)}
          reverting={revert.isPending}
        />
      ))}
    </div>
  );
};

const RevisionRow: React.FC<{
  page: WikiPageView;
  rev: WikiRevisionEntry;
  isCurrent: boolean;
  onKeep: () => void;
  onRevert: (toVersion: number) => void;
  reverting: boolean;
}> = ({ page, rev, isCurrent, onKeep, onRevert, reverting }) => {
  const [expanded, setExpanded] = useState(false);
  const { isDark } = useTheme();
  const isCreate = rev.changeType === 'create';

  const { data: diff, isLoading: diffLoading } = useQuery({
    queryKey: ['wikiDiff', page.id, rev.version],
    queryFn: () => wikiApi.getDiff(page.id, rev.version - 1, rev.version),
    enabled: expanded && !isCreate,
  });

  return (
    <div
      className={clsx(
        'rounded-[9px] border mb-2 overflow-hidden',
        rev.unseen ? 'border-info/40' : 'border-border',
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => !isCreate && setExpanded((e) => !e)}
        onKeyDown={(e) => e.key === 'Enter' && !isCreate && setExpanded((x) => !x)}
        className={clsx(
          'flex items-center gap-2.5 px-3.5 py-2.5 text-[12.5px]',
          !isCreate && 'cursor-pointer hover:bg-muted/40',
        )}
      >
        {rev.unseen && <span className="w-[7px] h-[7px] rounded-full bg-info flex-none" />}
        <span
          className={clsx(
            'text-[10px] font-bold rounded-[5px] px-1.5 py-px flex-none border',
            isCurrent
              ? 'bg-success/15 text-success border-transparent'
              : 'bg-muted text-foreground border-border',
          )}
        >
          v{rev.version}
        </span>
        <span className="inline-flex items-center gap-1.5 font-semibold text-[12px] flex-none">
          <span
            className={clsx(
              'w-4 h-4 rounded flex items-center justify-center text-[8px]',
              rev.changedByType === 'user'
                ? 'rounded-full bg-primary text-primary-foreground'
                : 'bg-muted border border-border',
            )}
          >
            {rev.changedByType === 'user'
              ? initials(rev.changedByName)
              : rev.changedByType === 'llm'
                ? '✦'
                : '⚙'}
          </span>
          {rev.changedByName}
        </span>
        <span className="flex-1 min-w-0 truncate text-[12px] text-muted-foreground">
          {rev.changeType === 'restore' && rev.summary ? rev.summary : rev.summary ?? '—'}
        </span>
        <span className="text-[10.5px] text-muted-foreground tabular-nums flex-none">
          {new Date(rev.createdAt).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
        {page.canEdit && (
          <span className="flex gap-1.5 flex-none" onClick={(e) => e.stopPropagation()}>
            {rev.unseen && (
              <button
                type="button"
                onClick={onKeep}
                className="text-[10.5px] font-semibold rounded-[7px] px-2.5 py-0.5 bg-success text-success-foreground"
              >
                ✓ Keep
              </button>
            )}
            {isCurrent && !isCreate && rev.version > 1 && (
              // Undo the change that produced the live version.
              <button
                type="button"
                disabled={reverting}
                onClick={() => onRevert(rev.version - 1)}
                className="text-[10.5px] font-semibold rounded-[7px] px-2.5 py-0.5 border border-input text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Revert
              </button>
            )}
            {!isCurrent && (
              // Bring this older version back as a new revision.
              <button
                type="button"
                disabled={reverting}
                onClick={() => onRevert(rev.version)}
                className="text-[10.5px] font-semibold rounded-[7px] px-2.5 py-0.5 border border-input text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Restore
              </button>
            )}
          </span>
        )}
      </div>

      {expanded && !isCreate && (
        <div className="border-t border-dashed border-border text-[11px] overflow-x-auto">
          {diffLoading || !diff ? (
            <div className="flex items-center justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : (
            <ReactDiffViewer
              oldValue={diff.from.content}
              newValue={diff.to.content}
              splitView={false}
              compareMethod={DiffMethod.WORDS}
              useDarkTheme={isDark}
              hideLineNumbers
            />
          )}
        </div>
      )}
    </div>
  );
};

function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default HistoryView;
